import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Lock, Plus } from 'lucide-react';
import { PlatformIcon } from '../common/PlatformIcon';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { useChannelConnect } from '../reach/ChannelPicker';
import { GET_SOCIAL_PROFILES, UPDATE_SOCIAL_PROFILE } from '../../graphql';
import { Platform } from '../../types';
import { GetSocialProfilesResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';

export function SocialSettings() {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { accountId, refetchAccounts } = useAccount();

  const { data: socialProfilesData, refetch } = useQuery<GetSocialProfilesResponse>(
    GET_SOCIAL_PROFILES,
    {
      variables: { accountId },
      skip: !accountId,
    }
  );

  // The sign-in popup comes back with the channels it can post to; the
  // person picks (several) or the one is connected at once. Rows exist only
  // then, so there is nothing to create before the popup.
  const { start: startChannelSignIn, picker: channelPicker } = useChannelConnect({
    accountId,
    onConnected: () => {
      refetch();
      refetchAccounts();
    },
  });

  const [updateSocialProfile] = useMutation(UPDATE_SOCIAL_PROFILE, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      console.error('Error updating social profile:', error);
    },
  });

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handlePlatformSelect = async (platform: string) => {
    if (!accountId) {
      return;
    }
    handleCloseDialog();
    await startChannelSignIn(platform);
  };

  const getPlatformColor = (platform: string | undefined): string => {
    switch (platform?.toLowerCase()) {
      case 'facebook':
        return '#1877F2';
      case 'instagram':
        return '#E4405F';
      case 'x':
        return '#000000';
      case 'threads':
        return '#000000';
      case 'youtube':
        return '#FF0000';
      case 'tiktok':
        return '#000000';
      case 'snapchat':
        return '#FFFC00';
      case 'reddit':
        return '#FF4500';
      case 'bluesky':
        return '#0560FF';
      case 'whatsapp':
        return '#25D366';
      case 'linkedin':
        return '#0A66C2';
      default:
        return '#f2efe0';
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 mb-6">
        <h2 className="mb-1">Connected Accounts</h2>
        <p className="text-muted-foreground">
          Manage your connected social media profiles and integrations.
        </p>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
          <h3 className="text-lg font-semibold text-cmp-ink">Social Accounts</h3>
          <Button onClick={handleOpenDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            New Social Account
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {socialProfilesData?.socialProfiles.map((profile) => (
              <div
                key={profile.id}
                className="p-4 flex flex-col items-center gap-2 relative bg-core-dark rounded shadow-lg hover:-translate-y-1 transition-transform duration-200"
              >
                {!profile.isActive && (
                  <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-error" />
                )}
                <div
                  className="w-12 h-12 flex items-center justify-center"
                  style={{ color: getPlatformColor(profile.platform) }}
                >
                  <PlatformIcon platform={profile.platform as Platform} size={48} />
                </div>
                <p className="mt-2 font-medium text-center text-cmp-ink capitalize">
                  {profile.profileName}
                </p>
                <p className="text-xs text-cmp-ink-2 text-center opacity-80">
                  Connected {new Date(profile.createdAt || '').toLocaleDateString()}
                </p>
                <div className="mt-auto pt-2 border-t border-cmp-line-2 w-full flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-cmp-ink">Active</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-medium ${
                          profile.isActive ? 'text-success' : 'text-cmp-ink-3'
                        }`}
                      >
                        {profile.isActive ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={profile.isActive}
                        onCheckedChange={(checked) => {
                          updateSocialProfile({
                            variables: {
                              id: profile.id,
                              input: {
                                profileName: profile.profileName,
                                profileUsername: profile.profileUsername || '',
                                profileLink: profile.profileLink || '',
                                isActive: checked,
                                isPublishing: profile.isPublishing,
                              },
                            },
                          });
                        }}
                        className={!profile.isActive ? 'border border-cmp-line-2' : ''}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-cmp-ink">Publishing</span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-medium ${
                          profile.isPublishing ? 'text-success' : 'text-cmp-ink-3'
                        }`}
                      >
                        {profile.isPublishing ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={profile.isPublishing}
                        onCheckedChange={(checked) => {
                          updateSocialProfile({
                            variables: {
                              id: profile.id,
                              input: {
                                profileName: profile.profileName,
                                profileUsername: profile.profileUsername || '',
                                profileLink: profile.profileLink || '',
                                isActive: profile.isActive,
                                isPublishing: checked,
                              },
                            },
                          });
                        }}
                        className={!profile.isPublishing ? 'border border-cmp-line-2' : ''}
                      />
                    </div>
                  </div>
                </div>
                {[
                  'x',
                  'facebook',
                  'threads',
                  'instagram',
                  'youtube',
                  'tiktok',
                  'snapchat',
                ].includes(profile.platform?.toLowerCase() || '') && (
                  <button
                    onClick={() => startChannelSignIn(profile.platform)}
                    className="mt-2 w-full px-3 py-1.5 border border-cmp-line-2 text-cmp-ink rounded text-xs hover:bg-form-dark flex items-center justify-center gap-1 transition-colors"
                  >
                    <Lock className="w-9" />
                    Reauthorize
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {openDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-2xl w-full m-4">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-cmp-ink mb-4">Add Social Account</h3>
              <p className="text-sm text-cmp-ink-2 mb-6">
                Select a platform to connect. Clicking an icon opens its sign-in; you then pick
                which accounts or pages to connect.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                {[
                  { platform: 'facebook', label: 'Facebook' },
                  { platform: 'x', label: 'X' },
                  { platform: 'threads', label: 'Threads' },
                  { platform: 'instagram', label: 'Instagram' },
                  { platform: 'youtube', label: 'YouTube' },
                  { platform: 'tiktok', label: 'TikTok' },
                  { platform: 'snapchat', label: 'Snapchat' },
                  { platform: 'reddit', label: 'Reddit' },
                  { platform: 'bluesky', label: 'BlueSky' },
                  { platform: 'whatsapp', label: 'WhatsApp' },
                  { platform: 'linkedin', label: 'LinkedIn' },
                ].map(({ platform, label }) => (
                  <button
                    key={platform}
                    onClick={() => handlePlatformSelect(platform)}
                    className="flex flex-col items-center gap-2 p-4 border border-cmp-line-2 rounded-lg hover:border-primary hover:bg-form-dark transition-all duration-200 group"
                  >
                    <div
                      className="w-12 h-12 flex items-center justify-center"
                      style={{ color: getPlatformColor(platform) }}
                    >
                      <PlatformIcon platform={platform as Platform} size={48} />
                    </div>
                    <span className="text-xs text-cmp-ink text-center group-hover:text-primary transition-colors">
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-cmp-line-2">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 text-cmp-ink border border-cmp-line-2 rounded hover:bg-form-dark transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {channelPicker}
    </div>
  );
}
